import { useCallback, useSyncExternalStore } from 'react'

const COOKIE_CHANGE_EVENT = 'vinc:cookie-change'

type BrowserCookieStore = EventTarget & {
  addEventListener: (
    type: 'change',
    listener: EventListenerOrEventListenerObject
  ) => void
  removeEventListener: (
    type: 'change',
    listener: EventListenerOrEventListenerObject
  ) => void
}

function getBrowserCookieStore(): BrowserCookieStore | null {
  if (typeof window === 'undefined') return null

  return (
    (window as Window & { cookieStore?: BrowserCookieStore }).cookieStore ??
    null
  )
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null

  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${name}=`))
    ?.split('=')[1]
  if (!cookie) return null

  try {
    return decodeURIComponent(cookie)
  } catch {
    // Tolerate legacy/unencoded cookie values.
    return cookie
  }
}

export function notifyCookieChange(): void {
  if (typeof window === 'undefined') return

  window.dispatchEvent(new Event(COOKIE_CHANGE_EVENT))
}

function subscribeToCookieChanges(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') return () => {}

  const cookieStore = getBrowserCookieStore()

  window.addEventListener(COOKIE_CHANGE_EVENT, onStoreChange)
  window.addEventListener('focus', onStoreChange)
  document.addEventListener('visibilitychange', onStoreChange)
  cookieStore?.addEventListener('change', onStoreChange)

  return () => {
    window.removeEventListener(COOKIE_CHANGE_EVENT, onStoreChange)
    window.removeEventListener('focus', onStoreChange)
    document.removeEventListener('visibilitychange', onStoreChange)
    cookieStore?.removeEventListener('change', onStoreChange)
  }
}

export const useCookie = () => {
  const getCookie = useCallback((name: string): string | null => {
    return readCookie(name)
  }, [])

  const setCookie = useCallback(
    (name: string, value: string, maxAge?: number) => {
      if (typeof window === 'undefined') return
      const isSecure = window.location.protocol === 'https:'

      const encodedValue = encodeURIComponent(value)
      let cookieString = `${name}=${encodedValue}; path=/; sameSite=lax`

      if (maxAge !== undefined) {
        cookieString += `; max-age=${maxAge}`
      }

      if (isSecure) {
        cookieString += '; secure'
      }

      document.cookie = cookieString
      notifyCookieChange()
    },
    []
  )
  return { getCookie, setCookie }
}

export const useCookieValue = (name: string): string | null => {
  const getSnapshot = useCallback(() => readCookie(name), [name])
  const getServerSnapshot = useCallback(() => null, [])

  return useSyncExternalStore(
    subscribeToCookieChanges,
    getSnapshot,
    getServerSnapshot
  )
}
